import type { Socket } from 'socket.io-client';
import { PromiseCompletion } from '../classes/PromiseCompletion';
import { notificationService } from './NotificationService';
import { onlineService } from './OnlineService';

type SocketSubscription = {
  room: string;
  data: unknown;
  topic: string;
  callback: Function;
};

class SocketClientService {
  private _disconnectTimeout: number | null = null;
  private _rooms: {room: string, dataJson: string | null}[] = [];
  private _socket: Socket | null = null;
  private _isConnected = false;
  private _subscriptions: SocketSubscription[] = [];
  private _loading = new PromiseCompletion();

  private async _ensureSocket(){
    if (this._disconnectTimeout){
      window.clearTimeout(this._disconnectTimeout);
      this._disconnectTimeout = null;
    }
    if (!this._socket){
      if (this._loading.isPending) {
        await this._loading.wait();
      } else {
        const ioLoader = import('socket.io-client');
        this._loading.subscribe(ioLoader);
        const io = await ioLoader;

        // Same-origin handshake carries the HttpOnly 'auth' cookie — no token in query.
        this._socket = io.connect();
        this._socket.on('connect', () => {
          this._rooms.forEach(room => {
            this._socket?.emit('join-' + room.room, room.dataJson ? JSON.parse(room.dataJson) : null);
          });
          this._isConnected = true;
        });
        this._socket.on('disconnect', (reason) => {
          this._isConnected = false;
          if (reason !== 'io client disconnect' && onlineService.isOnline) {
            notificationService.error(`Connection lost (${reason}). Changes may not be saved.`);
          }
        });
        this._socket.on('connect_error', (error) => {
          if (onlineService.isOnline) {
            notificationService.error(`Connection error: ${error.message}`);
          }
        });
      }
    }
  }
  
  public subscribe<T>(room: string, data: unknown, topic: string, onEvent: (data: T) => void){
    this._ensureSocket().then(() => {
      this._socket?.on(topic, onEvent);

      const dataJson = data ? JSON.stringify(data) : null;

      if (this._rooms.findIndex(r => r.room === room && r.dataJson === dataJson) === -1){
        this._rooms.push({
          room: room,
          dataJson: dataJson
        });
      }
      if (this._isConnected){
        this._socket?.emit('join-' + room, data);
      }
  
      this._subscriptions.push({
        room: room,
        data: data,
        topic: topic,
        callback: onEvent
      });
    }, (er) => {
      console.error(er);
    });
  }

  public unsubscribe(topic: string, data: unknown, onEvent: (data: unknown) => void){
    this._socket?.off(topic, onEvent);

    const subscription = this._subscriptions.find(x => x.topic === topic && x.callback === onEvent);
    if (subscription){
      const index = this._subscriptions.indexOf(subscription);
      if (index !== -1) {
        this._subscriptions.splice(index, 1);
      }

      if (this._subscriptions.findIndex(x => x.room === subscription.room && x.data === data) === -1){
        //this is the last event of this room
        this._socket?.emit('leave-' + subscription.room, subscription.data);

        const dataJson = data ? JSON.stringify(data) : null;
        const roomIndex = this._rooms.findIndex(r => r.room === subscription.room && r.dataJson === dataJson);
        if (roomIndex !== -1){
          this._rooms.splice(roomIndex, 1);
        }
      }
    }

    if (!this._subscriptions.length){
      this._disconnectTimeout = window.setTimeout(() => {
        this._disconnectTimeout = null;
        this._socket?.disconnect();
        this._socket = null;
      }, 2000);
    }
  }

  public emit<T>(message: string, data: T){
    this._ensureSocket().then(() => {
      this._socket?.emit(message, data);
    }, (er) => {
      notificationService.error('Failed to send message: ' + er.message);
    });
  }

  public on<T>(event: string, callback: (data: T) => void): void {
    this._ensureSocket().then(() => {
      this._socket?.on(event, callback as (data: unknown) => void);
    }, (er) => {
      console.error('Failed to subscribe to socket event:', er);
    });
  }

  public off(event: string, callback: Function): void {
    this._socket?.off(event, callback as (data: unknown) => void);
  }
}

export const socketClientService = new SocketClientService();